import React, { useEffect, useRef, useState } from "react";
import { ReactComponent as ArrowBottom } from "assets/icons/arrow_bottom.svg";
import SquireCheckbox from "components/Checkbox/SquireCheckobx";
import useSearch from "hooks/useSearch";
import { motion, useAnimation } from "framer-motion";
import { useClickAway } from "react-use";
import FilterButton from "components/Buttons/FilterButton";
import ReactDOM from "react-dom";

const PropertyTypeFilterDropdown = ({ style, id, buttonRef }) => {
  const [isCompraActive, setIsCompraActive] = useState(true);
  const dropdownRef = useRef(null);
  const [expandSubMenu, setExpandSubMenu] = useState(false);
  const {
    selectedPropertyType,
    setSelectedPropertyType,
    isFilterDropdownOpen,
    toggleFilterDropdown,
    propertyTypeDetails,
    setPropertyTypeDetails,
  } = useSearch();
  const controls = useAnimation();
  const isDropdownOpen = isFilterDropdownOpen === id;
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
  });

  // handle main menu checkboxes
  const handlePropertyTypeChange = (propertyId) => {
    setSelectedPropertyType(propertyId);

    if (propertyId === "Case - Appartamenti") {
      setPropertyTypeDetails((prevCheckboxes) =>
        prevCheckboxes.map((checkbox) => ({
          ...checkbox,
          checked: checkbox.id === "TutteLeTipologie",
        }))
      );
    } else {
      setPropertyTypeDetails((prevCheckboxes) =>
        prevCheckboxes.map((checkbox) => ({
          ...checkbox,
          checked: false,
        }))
      );
    }
  };

  // handle property type details changes
  const handlePropertyTypeDetailsChange = (id) => {
    setPropertyTypeDetails((prevPropertyTypeDetails) => {
      const isFirstPropertyTypeDetail = id === "TutteLeTipologie";
      let newPropertyTypeDetails;

      if (isFirstPropertyTypeDetail) {
        newPropertyTypeDetails = prevPropertyTypeDetails.map((checkbox) =>
          checkbox.id === id
            ? { ...checkbox, checked: !checkbox.checked }
            : { ...checkbox, checked: false }
        );
      } else {
        newPropertyTypeDetails = prevPropertyTypeDetails.map((checkbox) =>
          checkbox.id === id
            ? { ...checkbox, checked: !checkbox.checked }
            : checkbox.id === "TutteLeTipologie"
            ? { ...checkbox, checked: false }
            : checkbox
        );
      }

      if (selectedPropertyType !== "Case - Appartamenti") {
        setSelectedPropertyType("Case - Appartamenti");
      }

      const atLeastOneSelected = newPropertyTypeDetails.some(
        (checkbox) => checkbox.checked
      );
      if (!atLeastOneSelected) {
        return prevPropertyTypeDetails;
      }
      return newPropertyTypeDetails;
    });
  };

  // expand sub menu
  const handleExpandSubCheckbox = () => {
    setExpandSubMenu(!expandSubMenu);
  };

  const selectedSubChildCount = propertyTypeDetails.filter(
    (checkbox) => checkbox.checked
  ).length;

  const handleClickAway = (e) => {
    // Check if it click inside dropdown
    if (dropdownRef.current && dropdownRef.current.contains(e.target)) {
      return;
    }

    if (isDropdownOpen) {
      setDropdownPosition({ top: 0, left: 0 });
      toggleFilterDropdown(id);
    }
  };

  // close dropdown when click away
  useEffect(() => {
    document.addEventListener("click", handleClickAway);

    return () => {
      document.removeEventListener("click", handleClickAway);
    };
  }, [isDropdownOpen]);

  useEffect(() => {
    if (isDropdownOpen) {
      controls.start({ opacity: 1, y: 0 });
    } else {
      controls.start({ opacity: 0, y: id === "searchFilter" ? -10 : 0 });
    }
  }, [isDropdownOpen, controls]);

  const updateDropdownPosition = () => {
    if (buttonRef && buttonRef.current && isDropdownOpen) {
      const { top, left } = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: top + window.scrollY + buttonRef.current.offsetHeight,
        left: left + window.scrollX,
      });
    }
  };

  useEffect(() => {
    const handleResize = () => {
      updateDropdownPosition();
    };

    updateDropdownPosition();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isDropdownOpen]);

  const DropdownContent = () => (
    <>
      <div className="max-h-[400px] overflow-y-auto">
        <div>
          <div
            onClick={(event) => {
              if (event.target.tagName !== "INPUT") {
                handleExpandSubCheckbox();
              }
            }}
            className={`h-[60px] pl-[16px] cursor-pointer pr-[23px] flex items-center justify-between transition-all duration-300 ease-in-out hover:bg-[#F4F4F4] ${
              selectedPropertyType === "Case - Appartamenti"
                ? "bg-[#F4F4F4]"
                : ""
            }`}
          >
            <label
              htmlFor="CaseAppartamenti"
              className="flex h-full cursor-pointer items-center space-x-3 text-[20px] font-normal text-black"
            >
              <input
                className="hero-radio-input"
                type="radio"
                checked={selectedPropertyType === "Case - Appartamenti"}
                onChange={() => handlePropertyTypeChange("Case - Appartamenti")}
                // name="CaseAppartamenti"
                id="CaseAppartamenti"
              />
              Case - Appartamenti{" "}
              {selectedSubChildCount > 1 ? `• (${selectedSubChildCount})` : ""}
            </label>

            <ArrowBottom
              className={`transition-all duration-[.4s] ease-in-out ${
                expandSubMenu ? "-rotate-180" : "rotate-0"
              }`}
              width={14}
              strokeWidth={3}
            />
          </div>
          <div
            className={` space-y-[15px] transition-all duration-[0.4s] ease-in-out ${
              !expandSubMenu
                ? " max-h-0 overflow-hidden opacity-0"
                : "max-h-[1000px] mt-3 opacity-100"
            }`}
          >
            {propertyTypeDetails.map(({ id, label, checked }) => (
              <div
                key={id}
                className="hover:bg-[#F4F4F4] transition-all pl-[31px] duration-200 ease-in-out h-[52px]"
              >
                <SquireCheckbox
                  key={id}
                  id={id}
                  checked={checked}
                  label={label}
                  onChange={handlePropertyTypeDetailsChange}
                />
              </div>
            ))}
          </div>
        </div>

        <label
          htmlFor="Uffici"
          className={`${
            selectedPropertyType === "Uffici" ? "bg-[#F4F4F4]" : ""
          } h-[60px] pl-[16px] w-full cursor-pointer pr-[23px] flex transition-all duration-300 ease-in-out hover:bg-[#F4F4F4] items-center space-x-3 text-[20px] font-normal text-black`}
        >
          <input
            className="hero-radio-input"
            type="radio"
            // name="
            id="Uffici"
            checked={selectedPropertyType === "Uffici"}
            onChange={(e) => handlePropertyTypeChange("Uffici")}
          />
          Uffici
        </label>

        <label
          htmlFor="GaragePostiAuto"
          className={`${
            selectedPropertyType === "GaragePostiAuto" ? "bg-[#F4F4F4]" : ""
          } h-[60px] pl-[16px] w-full cursor-pointer pr-[23px] flex transition-all duration-300 ease-in-out hover:bg-[#F4F4F4] items-center space-x-3 text-[20px] font-normal text-black`}
        >
          <input
            className="hero-radio-input"
            type="radio"
            // name="GaragePostiAuto"
            id="GaragePostiAuto"
            checked={selectedPropertyType === "Garage - Posti Auto"}
            onChange={() => handlePropertyTypeChange("Garage - Posti Auto")}
          />
          Garage - Posti Auto
        </label>
        <label
          htmlFor="Negozi"
          className={`${
            selectedPropertyType === "Negozi" ? "bg-[#F4F4F4]" : ""
          } h-[60px] pl-[16px] w-full cursor-pointer pr-[23px] flex transition-all duration-300 ease-in-out hover:bg-[#F4F4F4] items-center space-x-3 text-[20px] font-normal text-black`}
        >
          <input
            className="hero-radio-input"
            type="radio"
            // name="
            id="Negozi"
            checked={selectedPropertyType === "Negozi"}
            onChange={(e) => handlePropertyTypeChange("Negozi")}
          />
          Negozi
        </label>
      </div>
      {id === "searchFilter" && <FilterButton />}
    </>
  );

  return (
    isDropdownOpen &&
    (id === "searchFilter" ? (
      ReactDOM.createPortal(
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={controls}
          transition={{ duration: 0.25 }}
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            ...style,
          }}
          ref={dropdownRef}
          className={`rounded-[20px] z-[9999] lg:z-50 max-h-[500px] overflow-y-hidden w-[calc(100%-62px)] md:w-[417px] absolute bg-white filter-dropdown-shadow `}
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownContent />
        </motion.div>,
        document.body
      ) // Portal rendering
    ) : (
      <motion.div
        initial={{ opacity: 0, y: 0 }}
        animate={controls}
        transition={{ duration: 0.25 }}
        style={{
          zIndex: id === "searchHeaderFilter" ? 51 : 50,
          ...style,
        }}
        ref={dropdownRef}
        className={`rounded-[20px] max-h-[500px] overflow-y-hidden w-full md:w-[417px] absolute bg-white property-type-dropdown-shadow`}
        onClick={(e) => e.stopPropagation()}
      >
        <DropdownContent />
      </motion.div>
    )) // Inline rendering
  );
};

export default PropertyTypeFilterDropdown;
